<template>
  <div class="view-wrapper">
    <div class="events__container">
      <div
        v-if="events.length > 0"
        class="events__tabs"
      >
        <a-tabs
          ref="tabs"
          class="search-view-tabs"
          :show-status-count="true"
          :status-list="tabs"
          :selected="tabSelected"
          :highlights-color="headingColor"
          :current-focus-idx="currentFocusTab"
          @switch-tabs="changeTab"
          @focus-next="focusNext"
        />
      </div>
      <div class="events__list">
        <div
          v-if="!filteredEvents.length && events.length"
          class="events-no-results"
        >
          {{ $t(`events.no${searchTabs[tabSelected].value}Search`, { searchQuery: searchText }) }}
          <EventsSkeleton />
        </div>
        <template v-else>
          <EventListItem
            v-for="event in filteredEvents"
            :key="event.id"
            :event-data="event"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useNuxtApp } from '#imports';
import isEmpty from 'lodash/isEmpty';
import ATabs from '../../../global/DSCL-copycats/Tabs.vue';
import EventListItem from './List/EventListItem.vue';
import EventsSkeleton from './shared/EventsSkeleton.vue';
import { SEARCH_TAB_LIST } from '../helpers/events.helper';
import { usePageHelpers } from '../../../../composables/usePageHelpers';
import { getEventSearchDateParams, getCurrentFormattedDate } from '../../../../helpers/events-state.helper';

defineOptions({ name: 'EventsSearchView' });

const { $axios, $intl } = useNuxtApp();
const store = useStore();
const route = useRoute();
const { isBottomVisible } = usePageHelpers();

const searchTabs = SEARCH_TAB_LIST($intl);
const tabSelected = ref(0);
const currentFocusTab = ref(0);
const pageNoPastEvents = ref(1);
const pageNoUpcomingEvents = ref(1);
const bottom = ref(false);
const events = ref([]);
const upcomingEvents = ref([]);
const pastEvents = ref([]);

const searchText = computed(() => store.state.events.searchText);
const allEvents = computed(() => store.state.events.allEvents);
const totalUpcomingEvents = computed(() => store.state.events.totalUpcomingEvents);
const totalPastEvents = computed(() => store.state.events.totalPastEvents);
const selectedCalendars = computed(() => store.state.events.selectedCalendars);
const sections = computed(() => store.state.events.sections);
const headingColor = computed(() => store.getters.getThemeHeadingColor);
const getLocale = computed(() => store.getters['translation/getLocale']);

function sameDay(d1, d2) {
  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
}

function getEvents(upcoming) {
  const currentDate = new Date();
  return events.value.filter(({ end_at, all_day }) => {
    const eventDate = new Date(end_at);
    if (upcoming) {
      return eventDate >= currentDate || (all_day && sameDay(currentDate, eventDate));
    }
    return eventDate < currentDate && !(all_day && sameDay(currentDate, eventDate));
  });
}

const filteredEvents = computed(() => {
  if (tabSelected.value === 0) {
    return getEvents(true);
  }
  return getEvents();
});

const tabs = computed(() =>
  searchTabs.map((tab) => {
    let countEvents;
    if (tab.value === 'Upcoming') {
      countEvents = totalUpcomingEvents.value;
    } else {
      countEvents = totalPastEvents.value;
    }
    return {
      ...tab,
      count: countEvents,
    };
  }),
);

const isMaxEvents = computed(() => {
  if (tabSelected.value === 0) {
    return upcomingEvents.value.length < 20 * pageNoUpcomingEvents.value;
  }
  return pastEvents.value.length < 20 * pageNoPastEvents.value;
});

watch(bottom, (isBottom) => {
  if (isBottom) {
    nextPage();
  }
});

let scrollHandler = null;

onMounted(() => {
  scrollHandler = () => {
    bottom.value = isBottomVisible();
  };
  window.addEventListener('scroll', scrollHandler);
  events.value = [...allEvents.value];
  if (isEmpty(selectedCalendars.value)) {
    store.commit(
      'events/initSelectedCalendars',
      sections.value.flatMap((item) => item.id),
    );
  }
});

onBeforeUnmount(() => {
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
  }
});

function focusNext(idx) {
  let nextSelectedIdx = 0;
  if (idx < 0) {
    nextSelectedIdx = searchTabs.length - 1;
  } else if (idx === searchTabs.length) {
    nextSelectedIdx = 0;
  } else {
    nextSelectedIdx = idx;
  }
  document.getElementById(`tab-${searchTabs[nextSelectedIdx].value}`).focus();
  currentFocusTab.value = searchTabs[nextSelectedIdx].idx;
}

function changeTab(tab) {
  tabSelected.value = tab;
}

async function nextPage() {
  if (isMaxEvents.value) return;

  let pageNo;
  if (tabSelected.value === 0) {
    pageNoUpcomingEvents.value += 1;
    pageNo = pageNoUpcomingEvents.value;
  } else {
    pageNoPastEvents.value += 1;
    pageNo = pageNoPastEvents.value;
  }

  try {
    const { url } = store.getters.getEventsApi($axios.defaults.baseURL);
    const res = await $axios.get(url, {
      params: {
        ...route.query,
        ...getEventSearchDateParams(tabSelected.value === 0 ? 0 : 6),
        ...(tabSelected.value !== 0 ? getCurrentFormattedDate() : { end_date: undefined }),
        paginate: true,
        page_no: pageNo,
        locale: getLocale.value,
      },
      cache: { ignoreCache: false },
    });
    bottom.value = false;

    if (tabSelected.value === 0) {
      upcomingEvents.value = [...upcomingEvents.value, ...res.data.events];
    } else {
      pastEvents.value = [...pastEvents.value, ...res.data.events];
    }
    events.value = [...events.value, ...res.data.events];
  } catch (e) {
    console.error(e);
  }
}
</script>

<style lang="scss" scoped>
@use '../../../global/scss/mixins/breakpoints';

.a-tabs.search-view-tabs {
  justify-content: flex-start;
  margin-top: 24px;
  border-top: 1px solid rgba(0, 73, 144, 0.1);
  @include breakpoints.screen('xs') {
    :deep(.tab) {
      width: 180px;
    }
  }
}
.events-no-results {
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  letter-spacing: 0.8px;
  margin-top: 40px;
}
</style>
